import React, { useState, useEffect, useCallback } from 'react';
import Lenis from 'lenis';
import 'lenis/dist/lenis.css';
import HeroSection from './components/HeroSection';
import AboutSection from './components/AboutSection';
import SolutionsSection from './components/SolutionsSection';
import WhyChooseSection from './components/WhyChooseSection';
import RecognitionSection from './components/RecognitionSection';
import CtaBanner from './components/CtaBanner';
import Footer from './components/Footer';
import ContactPage from './components/ContactPage';
import AboutPage from './components/AboutPage';
import ServicesPage from './components/ServicesPage';
import LifeInsurancePage from './components/LifeInsurancePage';
import CustomerFormPage from './components/CustomerFormPage';
import './App.css';

// Route configuration mapping page identifiers to URLs and titles
export const ROUTE_CONFIG = {
  home: {
    path: '/',
    title: 'Meena Insurance & Investments | Expert Financial Planning & Insurance Advisor',
  },
  about: {
    path: '/about',
    title: 'About Us | Meena Insurance & Investments',
  },
  services: {
    path: '/services',
    title: 'Services | Meena Insurance & Investments',
  },
  'life-insurance': {
    path: '/life-insurance',
    title: 'Life Insurance | Meena Insurance & Investments',
  },
  contact: {
    path: '/contact',
    title: 'Contact Us | Meena Insurance & Investments',
  },
  'customer-form': {
    path: '/customer-form',
    title: 'LIC Customer Bio-Data Form | Meena Insurance & Investments',
  },
};

// URL path to internal page identifier lookup
const PATH_TO_PAGE = {
  '/': 'home',
  '/home': 'home',
  '/about': 'about',
  '/about-us': 'about',
  '/about-me': 'about',
  '/services': 'services',
  '/life-insurance': 'life-insurance',
  '/lifeinsurance': 'life-insurance',
  '/contact': 'contact',
  '/contact-us': 'contact',
  '/customer-form': 'customer-form',
  '/customerform': 'customer-form',
};

// Resolves current page from browser URL pathname
const getPageFromLocation = () => {
  const publicUrl = process.env.PUBLIC_URL || '';
  let pathname = window.location.pathname || '/';

  if (publicUrl && pathname.startsWith(publicUrl)) {
    pathname = pathname.slice(publicUrl.length);
  }

  // Ensure leading slash
  if (!pathname.startsWith('/')) {
    pathname = '/' + pathname;
  }

  // Strip trailing slash if not root
  if (pathname.length > 1 && pathname.endsWith('/')) {
    pathname = pathname.slice(0, -1);
  }

  const page = PATH_TO_PAGE[pathname.toLowerCase()];
  return page || 'home';
};

function App() {
  const [currentPage, setCurrentPage] = useState(getPageFromLocation);

  // Sync document title with current page
  useEffect(() => {
    const config = ROUTE_CONFIG[currentPage];
    if (config && config.title) {
      document.title = config.title;
    }
  }, [currentPage]);

  // Handle browser Back / Forward buttons (popstate)
  useEffect(() => {
    const handlePopState = () => {
      const page = getPageFromLocation();
      setCurrentPage(page);

      const hash = window.location.hash ? window.location.hash.replace('#', '') : null;
      if (hash) {
        setTimeout(() => {
          const el = document.getElementById(hash);
          if (el) {
            const navbarHeight = 80;
            const elementPosition = el.getBoundingClientRect().top + window.pageYOffset;
            window.scrollTo({
              top: elementPosition - navbarHeight,
              behavior: 'smooth',
            });
          }
        }, 120);
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Handle initial page load with potential hash anchor (e.g., /#solutions)
  useEffect(() => {
    if (window.location.hash) {
      const hash = window.location.hash.replace('#', '');
      setTimeout(() => {
        const el = document.getElementById(hash);
        if (el) {
          const navbarHeight = 80;
          const elementPosition = el.getBoundingClientRect().top + window.pageYOffset;
          window.scrollTo({
            top: elementPosition - navbarHeight,
            behavior: 'smooth',
          });
        }
      }, 250);
    }
  }, []);

  // Initialize Lenis smooth scroll for Webflow-like physics
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: 'vertical',
      gestureOrientation: 'vertical',
      smoothWheel: true,
      wheelMultiplier: 1.0,
      touchMultiplier: 1.8,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    return () => {
      if (lenis && typeof lenis.destroy === 'function') {
        lenis.destroy();
      }
    };
  }, [currentPage]);

  // Navigate function that changes both state and browser URL bar
  const handleNavigate = useCallback((page, sectionId) => {
    const targetPage = PATH_TO_PAGE[page] || (ROUTE_CONFIG[page] ? page : 'home');
    const config = ROUTE_CONFIG[targetPage] || ROUTE_CONFIG.home;
    const publicUrl = process.env.PUBLIC_URL || '';
    const newPath = `${publicUrl}${config.path}${sectionId ? `#${sectionId}` : ''}`;

    const currentFullPath = window.location.pathname + window.location.hash;
    if (currentFullPath !== newPath) {
      window.history.pushState({ page: targetPage, sectionId }, '', newPath);
    }

    setCurrentPage(targetPage);

    if (sectionId) {
      setTimeout(() => {
        const el = document.getElementById(sectionId);
        if (el) {
          const navbarHeight = 80;
          const elementPosition = el.getBoundingClientRect().top + window.pageYOffset;
          window.scrollTo({
            top: elementPosition - navbarHeight,
            behavior: 'smooth',
          });
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  return (
    <div className="App">
      {currentPage === 'home' && (
        <>
          <HeroSection onNavigate={handleNavigate} />
          <AboutSection onNavigate={handleNavigate} />
          <SolutionsSection onNavigate={handleNavigate} />
          <WhyChooseSection />
          <RecognitionSection />
          <CtaBanner onNavigate={handleNavigate} />
          <Footer onNavigate={handleNavigate} />
        </>
      )}
      {currentPage === 'services' && (
        <ServicesPage onNavigate={handleNavigate} />
      )}
      {currentPage === 'life-insurance' && (
        <LifeInsurancePage onNavigate={handleNavigate} />
      )}
      {currentPage === 'about' && (
        <AboutPage onNavigate={handleNavigate} />
      )}
      {currentPage === 'contact' && (
        <ContactPage onNavigate={handleNavigate} />
      )}
      {currentPage === 'customer-form' && (
        <CustomerFormPage onNavigate={handleNavigate} />
      )}
    </div>
  );
}

export default App;
